<template>
  <v-container fluid class="pa-0">
    <v-row no-gutters>
      <v-col class="ma-4">
        <v-card class="pa-4 pt-0">
          <v-progress-linear v-if="loadingStampede" absolute :indeterminate="loadingStampede" />
          <h4 class="pt-4">API Stampede Test</h4>
          <div>
            This test simulates a burst of API calls ({{ burstCount }})
            with an expired access token to verify that only one token refresh
            is performed and all requests are retried successfully.
          </div>
          <v-btn class="my-4" color="primary" :disabled="loadingStampede" :loading="loadingStampede"
            @click="testStampede()">
            Run Test
          </v-btn>
          <div>
            Results:
            <b v-if="!loadingStampede && resultStampede" :class="stampedeTestSuccess ? 'success--text' : 'error--text'">
              {{ stampedeTestSuccess ? 'Success' : 'Failed' }}
            </b>
            <pre>{{ pretty(resultStampede) }}</pre>
          </div>
        </v-card>
      </v-col>

      <v-col class="ma-4">
        <v-card class="pa-4 pt-0">
          <v-progress-linear v-if="loadingInFlight" absolute :indeterminate="loadingInFlight" />
          <h4 class="pt-4">API In-flight De-duplication Test</h4>
          <div>
            This test fires {{ burstCount }} identical GETs at the same time
            and verifies that only one network request is sent (others share it).
          </div>
          <v-btn class="my-4" color="primary" :disabled="loadingInFlight" :loading="loadingInFlight"
            @click="testInFlight()">
            Run Test
          </v-btn>
          <div>
            Results:
            <b v-if="!loadingInFlight && resultInFlight" :class="inFlightTestSuccess ? 'success--text' : 'error--text'">
              {{ inFlightTestSuccess ? 'Success' : 'Failed' }}
            </b>
            <pre>{{ pretty(resultInFlight) }}</pre>
          </div>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import makeClient from '@/api/client.js';

export default {
  name: 'ClientApiTestPage',
  data() {
    return {
      burstCount: 100,

      loadingStampede: false,
      resultStampede: null,

      loadingInFlight: false,
      resultInFlight: null,
    };
  },

  mounted() {
    // this.runAllTests();
  },

  methods: {
    runAllTests() {
      this.testStampede();
      this.testInFlight();
    },

    pretty(obj) {
      return obj ? JSON.stringify(obj, null, 2) : '';
    },

    async testStampede() {
      this.loadingStampede = true;
      const start = performance.now();
      const client = makeClient();
      client.__TEST_METRICS__.reset();

      // Simulate that the access token just expired for the first wave only
      let simulateOnce = true;
      const devInterceptorId = client.interceptors.request.use((config) => {
        if (simulateOnce && !config.url.includes('/api2/token/refresh')) {
          if (!config._simulated) {
            config._simulated = true;
            const error = new Error('Synthetic 401 for test');
            error.config = config;
            error.response = { status: 401, data: { Message: 'Expired' }, config };
            return Promise.reject(error);
          }
        }
        return config;
      });

      // After scheduling the burst, allow retries to pass
      setTimeout(() => { simulateOnce = false; }, 0);

      const req = () => client.get('/api2/Report/logo', {
        // vary the query so de-dup does NOT collapse this burst
        params: { t: Math.random() },
        headers: { 'Cache-Control': 'no-cache' },
      });

      let responses = [];
      let allSuccess = false;

      try {
        const jobs = [];
        for (let i = 0; i < this.burstCount; i++) jobs.push(req());
        responses = await Promise.all(jobs);
        allSuccess = responses.every(r => r.status === 200);
      } catch (e) {
        console.error('Stampede test failed:', e);
      } finally {
        client.interceptors.request.eject(devInterceptorId);
        const timing = ((performance.now() - start) / 1000).toFixed(2);
        const metrics = client.__TEST_METRICS__;
        this.resultStampede = {
          timing: `${timing}s`,
          allSuccess,
          responseCount: responses.length, // expected == burstCount
          refreshCount: metrics.refreshCount, // expected == 1
        };
        this.loadingStampede = false;
      }
    },

    async testInFlight() {
      this.loadingInFlight = true;
      const start = performance.now();
      const client = makeClient();
      client.__TEST_METRICS__.reset();

      const url = '/api2/Report/logo';

      // This key must match generateCacheKey() format in client.js (no params here):
      const keyForCount = `GET:${new URL(url, 'http://dummy').pathname}`;

      const req = () => client.get(url, {
        // IDENTICAL request so de-dup collapses the burst
        headers: { 'Cache-Control': 'no-cache' },
      });

      let responses = [];
      let allSuccess = false;

      try {
        const jobs = [];
        for (let i = 0; i < this.burstCount; i++) jobs.push(req());
        responses = await Promise.all(jobs);
        allSuccess = responses.every(r => r.status === 200);
      } catch (e) {
        console.error('In-flight test failed:', e);
      } finally {
        const timing = ((performance.now() - start) / 1000).toFixed(2);
        const metrics = client.__TEST_METRICS__;
        this.resultInFlight = {
          timing: `${timing}s`,
          allSuccess,
          responseCount: responses.length,    // expected == burstCount
          networkHitsForKey: metrics.get(keyForCount), // expected == 1
        };
        this.loadingInFlight = false;
      }
    },
  },

  computed: {
    stampedeTestSuccess() {
      // success if we got all responses and fewer than 1 refresh per request
      return !this.loadingStampede && this.resultStampede && this.resultStampede.refreshCount < this.burstCount;
    },

    inFlightTestSuccess() {
      // success if we got all responses and only 1 network hit
      return !this.loadingInFlight && this.resultInFlight && this.resultInFlight.networkHitsForKey === 1;
    }

  },
};
</script>

<style scoped>
pre {
  background: #111;
  color: #eee;
  padding: 12px;
  border-radius: 8px;
  overflow: auto;
  max-height: 300px;
  font-size: 12px;
}
</style>